import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { FileSignature, Download, CheckCircle, Clock, AlertCircle, Eye, PenTool } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';

export default function SignableDocumentsWorkerPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await API.get('/signable-documents');
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/signable-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const initiateSign = (doc) => {
    setSelectedDoc(doc);
    setSignDialogOpen(true);
    setMessage(null);
  };

  const handleSignature = async (signatureData) => {
    if (!selectedDoc) return;
    setSigning(true);
    
    try {
      await API.post(`/signable-documents/${selectedDoc.id}/sign`, { signature: signatureData });
      setMessage({ type: 'success', text: 'Documento firmado correctamente' });
      setSignDialogOpen(false);
      fetchDocuments();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al firmar documento' });
    } finally {
      setSigning(false);
    }
  };

  const pendingDocs = documents.filter(d => !d.is_signed);
  const signedDocs = documents.filter(d => d.is_signed);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signable-documents-worker-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900" >
          Documentos para Firmar
        </h1>
        <p className="text-gray-500 mt-1 text-sm sm:text-base">Revisa y firma los documentos de tu empresa</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-md ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Pending Documents */}
      {pendingDocs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pendientes de firma ({pendingDocs.length})
          </h2>
          <div className="grid gap-4">
            {pendingDocs.map((doc) => (
              <div key={doc.id} className="bg-white border-2 border-amber-200 rounded-lg p-4 sm:p-6" data-testid={`pending-doc-${doc.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileSignature className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                      {doc.description && <p className="text-sm text-gray-500 mt-1">{doc.description}</p>}
                      <p className="text-xs text-gray-400 mt-2">
                        Recibido: {new Date(doc.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:flex-col">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      className="flex-1 sm:flex-none"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => initiateSign(doc)}
                      className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700"
                    >
                      <PenTool className="w-4 h-4 mr-1" />
                      Firmar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signed Documents */}
      {signedDocs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            Firmados ({signedDocs.length})
          </h2>
          <div className="grid gap-4">
            {signedDocs.map((doc) => (
              <div key={doc.id} className="bg-white border border-orange-100 rounded-lg p-4 sm:p-6" data-testid={`signed-doc-${doc.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileSignature className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Firmado</Badge>
                      </div>
                      {doc.description && <p className="text-sm text-gray-500 mt-1">{doc.description}</p>}
                      <p className="text-xs text-gray-400 mt-2">
                        Recibido: {new Date(doc.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Descargar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="bg-white border border-orange-100 rounded-lg p-12 text-center">
          <FileSignature className="w-12 h-12 text-orange-200 mx-auto mb-4" />
          <p className="text-gray-500">No tienes documentos pendientes de firma</p>
        </div>
      )}

      {/* Sign Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Firmar Documento
            </DialogTitle>
            <DialogDescription>
              {selectedDoc?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                Al firmar este documento, confirmas que has leído y aceptas su contenido.
              </p>
            </div>
            
            <SignaturePad
              onSave={handleSignature}
              onCancel={() => {
                setSignDialogOpen(false);
                setSelectedDoc(null);
              }}
              title="Dibuja tu firma"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
